import React from "react";
import styled, { createGlobalStyle } from "styled-components";
import {
  Color,
  FontSize,
  FontWeight,
  GlossyThemeProvider,
  Spacing,
  ZIndexes,
  background,
  content,
  cover,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Button from "@ui-kit/button";
import Icon from "@ui-kit/icon";
import SafeHTML from "@ui-kit/safe-html";
import Panel from "@radarly/components/panel/fullscreen";

const HEADER_HEIGHT = 124;

interface IProps {
  children: React.ReactNode;
  onClose: () => void;
  locale: Locale;
  field: string;
  variable: string;
  query: string;
  subtitle: string;
  title: string;
}

const GlobalStyle = createGlobalStyle`
  #main {
    overflow: hidden;
  }
`;

const Root = styled.div`
  ${cover()};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.Large};
`;

const Header = styled.div`
  display: flex;
  gap: ${Spacing.Large};
  top: 0;
  position: absolute;
  width: 100%;
  z-index: ${ZIndexes.AboveContent};
  height: ${HEADER_HEIGHT}px;
  background: ${background};
  padding: 12px 16px;
  align-items: center;
  box-shadow: ${transparentize(0.8, Color.Black)} 0 1px 2px 0;
`;

const Content = styled.div`
  padding: 0 32px;
  width: 100vw;
  overflow: auto;
  color: ${content};
  background-color: ${background};
  height: calc(100vh - ${HEADER_HEIGHT}px);
  margin-top: ${HEADER_HEIGHT}px;
`;

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`;

const Titles = styled.div`
  min-width: 0;
`;

const Title = styled(SafeHTML)`
  font-size: ${FontSize.Large};
  font-weight: ${FontWeight.Bold};
  color: ${content};

  span.highligthed {
    color: ${Color.PacificBlue};
    display: inline-flex;
    height: 20px;
  }
`;

const Subtitle = styled.div`
  font-size: ${FontSize.Medium};
  font-weight: ${FontWeight.Normal};
  color: ${(props) => content(props, "secondary")};
`;

const RadarlyPanelsDiscoveryDetails = ({
  children,
  onClose,
  locale,
  field,
  variable,
  query,
  subtitle,
  title,
}: IProps) => (
  <GlossyThemeProvider name="body">
    <GlobalStyle />

    <Panel>
      <Root>
        <GlossyThemeProvider name="surface">
          <Header>
            <Button
              icon="arrow-left"
              onClick={onClose}
              size="m"
              variant="tertiary"
            />
            <StyledIcon glyph="lightbulb" size={76} />
            <Titles>
              <Title html={title} />
              {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </Titles>
          </Header>
        </GlossyThemeProvider>
        <Content>{children}</Content>
      </Root>
    </Panel>
  </GlossyThemeProvider>
);

export default RadarlyPanelsDiscoveryDetails;
